<template>
  <div>
    <div
      ref="markerEl"
      class="w-10 group cursor-pointer"
      @click="emit('click')">
      <Pin
        v-if="!marker.locations || marker.locations.length <= 1"
        class="transition-all duration-300 ease-smooth group-hover:scale-110 group-hover:fill-accent-100"
        :domain="marker.domain" />
      <MapCluster v-else :count="marker.locations.length" :active="isHighlighted" />
    </div>
    <div ref="popupEl" class="flex flex-col items-start gap-2">
      <MapPopup
        v-if="currentLocation"
        :location="currentLocation"
        @close="isPopupOpen = false"
        @click-location="emit('click-location', $event, currentLocation)"
      />
      <MapPopupControls v-if="marker.locations && marker.locations.length > 1" v-model="currentLocationIndex" :marker="marker" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Location } from '@corporate/components/LocationCard/LocationCard.vue'
import type { Domain } from '@gro-up/types'
import { useResponsive } from '@gro-up/ui/composables'
import { Pin } from '@gro-up/ui/primitives'
import { onKeyUp } from '@vueuse/core'
import type { Map, Popup as MapboxPopup, Marker as MapboxMarker } from 'mapbox-gl'
import { ref, shallowRef, onMounted, onUnmounted, computed, watch } from 'vue'

import MapCluster from './MapCluster.vue'
import MapPopup from './MapPopup.vue'
import MapPopupControls from './MapPopupControls.vue'

export type LocationMarker = {
  latitude: number
  longitude: number
  domain: Domain
  locations?: Location[]
}

const props = defineProps<{
  marker: LocationMarker
  map: Map
  hovered?: boolean
  selected?: boolean
  readonly?: boolean
}>()

const emit = defineEmits(['click', 'click-location'])

const { isMaxMd } = useResponsive()

const markerEl = ref<HTMLElement | null>(null)
const popupEl = ref<HTMLElement | null>(null)
const popup = shallowRef<MapboxPopup | null>(null)
const marker_ = shallowRef<MapboxMarker | null>(null)

const currentLocationIndex = ref(0)
const isPopupOpen = ref(false)

const currentLocation = computed(() => props.marker.locations?.[currentLocationIndex.value])

// Mapbox relocates `markerEl` out of Vue's DOM tree, which makes vnode-driven class
// patches on the marker unreliable. Toggle the highlight classes imperatively instead so
// hover/selection state always reflects on the relocated element.
const isHighlighted = computed(() => isPopupOpen.value || !!props.hovered || !!props.selected)

const applyHighlight = (highlighted: boolean) => {
  // Clusters render a MapCluster badge that handles its own highlight via the reactive `active`
  // prop. Skip the imperative toggle there so we don't compound the scale or override the bubble fill.
  if (props.marker.locations && props.marker.locations.length > 1) {
    return
  }

  const svg = markerEl.value?.querySelector('svg')
  svg?.classList.toggle('scale-110', highlighted)
  svg?.classList.toggle('fill-accent-100', highlighted)
}

watch(isHighlighted, applyHighlight)

onKeyUp('Escape', () => popup.value?.remove())

onMounted(async () => {
  if (!markerEl.value || !popupEl.value) {
    return
  }

  const mapboxgl = await import('mapbox-gl')

  popup.value = new mapboxgl.Popup({
    offset: [0, -20],
    className: 'custom-popup-wrapper',
    closeButton: false,
    anchor: 'bottom',
    maxWidth: isMaxMd.value ? '300px' : '360px',
  })

  if (!props.readonly) {
    popup.value.setDOMContent(popupEl.value)
  }

  popup.value.on('open', () => (isPopupOpen.value = true))
  popup.value.on('close', () => (isPopupOpen.value = false))

  marker_.value = new mapboxgl.Marker({
    element: markerEl.value,
    anchor: 'center',
  })
    .setLngLat([props.marker.longitude, props.marker.latitude])
    .addTo(props.map)

  if (!props.readonly) {
    marker_.value.setPopup(popup.value as unknown as MapboxPopup)
  }

  applyHighlight(isHighlighted.value)
})

onUnmounted(() => {
  popup.value?.remove()
  marker_.value?.remove()
})

watch(isPopupOpen, value => {
  if (!value) {
    popup.value?.remove()
  }
})
</script>

<style>
.custom-popup-wrapper .mapboxgl-popup-tip {
  display: none;
}

.custom-popup-wrapper .mapboxgl-popup-content {
  background: none;
  box-shadow: none;
  padding: 10px;
  pointer-events: none;
}
</style>
